import "navigator.locks";
import type { Accessor, ContextProviderComponent, Resource } from "solid-js";
import { createContext, createMemo, createResource, createSignal } from "solid-js";
import type { User } from "oidc-client-ts";
import { UserManager, WebStorageStateStore } from "oidc-client-ts";
import { AUTH_APP_ID, AUTH_BASE_URL } from "../constants";
import { isNil } from "lodash-es";
import { initializedContext } from "../utils/initializedUtil";
import type CustomerId from "./CustomerId";
import logger from "../utils/logger";

export type LoginStatus = "loggedIn" | "loading" | "loggedOut";

export type Impersonation = { type: "customer"; value: CustomerId } | { type: "user"; value: string };

type State = {
  loginStatus: Accessor<LoginStatus>;
  user: Resource<User | null>;
  impersonation: Accessor<Impersonation | undefined>;
};

type Dispatch = {
  login: (origin?: string) => Promise<void>;
  logout: (origin?: string) => Promise<void>;
  handleLoginCallback: () => Promise<string>;
  handleLogoutCallback: () => Promise<string>;
  refreshAccessToken: () => Promise<string>;
  getCurrentUser: () => Promise<User | null>;
  impersonateUser: (userId: string) => void;
  impersonateCustomer: (customerId: CustomerId) => void;
  stopImpersonation: () => void;
};

const RESPONSE_TYPE = "code";
const SCOPE = "openid offline";

const StateContext = createContext<State>();
const DispatchContext = createContext<Dispatch>();

export const AuthProvider: ContextProviderComponent<undefined> = (props) => {
  const userManager = new UserManager({
    authority: AUTH_BASE_URL,
    client_id: AUTH_APP_ID,
    redirect_uri: `${window.location.origin}/login/callback`,
    post_logout_redirect_uri: `${window.location.origin}/logout/callback`,
    response_type: RESPONSE_TYPE,
    scope: SCOPE,
    automaticSilentRenew: false,
    userStore: new WebStorageStateStore({ store: window.localStorage }),
  });

  const [user, { mutate }] = createResource(() => userManager.getUser());
  const loginStatus = createMemo<LoginStatus>(() => {
    if (user.loading) {
      return "loading";
    }
    return isNil(user.latest) ? "loggedOut" : "loggedIn";
  });
  const [impersonation, setImpersonation] = createSignal<Impersonation>();

  const login = async (origin?: string) => {
    await userManager.signinRedirect({ state: origin });
  };

  const logout = async (origin?: string) => {
    const idToken = user.latest?.id_token;
    await userManager.removeUser();
    await userManager.signoutRedirect({ id_token_hint: idToken, state: origin });
    mutate(null);
  };

  const handleLoginCallback = async () => {
    try {
      const user = await userManager.signinCallback();
      mutate(user);

      return !isNil(user) ? (user?.state as string) : "/";
    } catch (e) {
      await userManager.removeUser();
      await userManager.clearStaleState();
      throw e;
    }
  };

  const handleLogoutCallback = async () => {
    const signoutResponse = await userManager.signoutRedirectCallback();
    return !isNil(signoutResponse.userState) ? (signoutResponse.userState as string) : "/";
  };

  const refreshAccessToken = async (): Promise<string> =>
    await navigator.locks.request("moreapp_platform_access_token_refresh", async () => {
      logger.debug("Refreshing access token");

      try {
        const user = await userManager.signinSilent();
        if (isNil(user)) {
          await logout();
          return Promise.reject("Failed to refresh access token");
        }

        mutate(user);
        return user.access_token;
      } catch (e) {
        await logout();
        throw e;
      }
    });

  const getCurrentUser = async () => userManager.getUser();

  const impersonateUser = (userId: string) => setImpersonation({ type: "user", value: userId });

  const impersonateCustomer = (customerId: CustomerId) => setImpersonation({ type: "customer", value: customerId });

  const stopImpersonation = () => {
    setImpersonation(undefined);
    window.location.href = "/"; // Forces a browser reload, and thus also clearing local cache
  };

  const state = {
    loginStatus,
    user,
    impersonation,
  };

  const dispatch: Dispatch = {
    login,
    logout,
    handleLoginCallback,
    handleLogoutCallback,
    refreshAccessToken,
    getCurrentUser,
    impersonateUser,
    impersonateCustomer,
    stopImpersonation,
  };

  return (
    <StateContext.Provider value={state}>
      <DispatchContext.Provider value={dispatch}>{props.children}</DispatchContext.Provider>
    </StateContext.Provider>
  );
};

export const useAuthState = () => initializedContext(StateContext, "useAuthState", "AuthProvider");
export const useAuthDispatch = () => initializedContext(DispatchContext, "useAuthDispatch", "AuthProvider");
